import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, CalendarClock, Navigation, History, Clock, Calendar, MapPin, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const statusLabels = {
  pending: { text: "En attente", cls: "bg-yellow-500/20 text-yellow-400" },
  confirmed: { text: "Confirmee", cls: "bg-emerald-500/20 text-emerald-400" },
  in_progress: { text: "En cours", cls: "bg-blue-500/20 text-blue-400" },
  completed: { text: "Terminee", cls: "bg-zinc-700 text-zinc-300" },
  cancelled: { text: "Annulee", cls: "bg-red-500/20 text-red-400" },
};

const BookingCard = ({ booking, onClick }) => {
  const dt = booking.pickup_datetime || booking.pickup_date;
  const pickupDate = dt ? new Date(dt) : new Date();
  const st = statusLabels[booking.status] || statusLabels.pending;

  return (
    <button
      onClick={onClick}
      className="w-full text-left glass-card rounded-xl p-4 border border-zinc-800 hover:border-zinc-600 transition-all active:scale-[0.98]"
      data-testid={`booking-card-${booking.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-white text-sm font-medium">
              {format(pickupDate, "EEE d MMM", { locale: fr })}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-white text-sm">{format(pickupDate, "HH:mm")}</span>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.text}</span>
      </div>
      <div className="flex gap-3">
        <div className="flex flex-col items-center pt-1">
          <div className="w-2 h-2 bg-emerald-500 rounded-full" />
          <div className="w-0.5 h-6 bg-zinc-700" />
          <div className="w-2 h-2 bg-red-500 rounded-full" />
        </div>
        <div className="flex-1 space-y-1 min-w-0">
          <p className="text-xs text-zinc-300 truncate">{booking.pickup_address}</p>
          <p className="text-xs text-zinc-300 truncate">{booking.destination_address}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-emerald-500 font-bold text-sm">{booking.price?.toFixed(2) || booking.total_price || "—"}€</span>
          <ChevronRight className="w-4 h-4 text-zinc-500" />
        </div>
      </div>
    </button>
  );
};

const headers = {
  upcoming: { title: "Courses a venir", icon: CalendarClock, color: "text-blue-400" },
  current: { title: "Course en cours", icon: Navigation, color: "text-yellow-400" },
  history: { title: "Historique", icon: History, color: "text-purple-400" },
};

export const ClientBookingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeader } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const segment = location.pathname.split("/").pop();
  const meta = headers[segment] || headers.upcoming;
  const Icon = meta.icon;

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/bookings`, { headers: getAuthHeader() });
        const all = res.data || [];
        const now = new Date();
        let filtered;
        if (segment === "upcoming") {
          filtered = all.filter((b) => new Date(b.pickup_datetime) > now && !["cancelled", "completed"].includes(b.status));
        } else if (segment === "current") {
          filtered = all.filter((b) => b.status === "in_progress");
        } else {
          filtered = all.filter((b) => ["completed", "cancelled"].includes(b.status) || new Date(b.pickup_datetime) < now);
        }
        setBookings(filtered);
      } catch {
        setBookings([]);
      }
      setLoading(false);
    };
    fetchBookings();
  }, [segment, getAuthHeader]);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="px-4 py-4 border-b border-zinc-800">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate("/client")} className="p-2 rounded-lg hover:bg-zinc-800 transition-colors" data-testid="back-client-btn">
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <Icon className={`w-5 h-5 ${meta.color}`} />
          <h1 className="text-lg font-bold text-white font-['Outfit']">{meta.title}</h1>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16">
            <Icon className={`w-12 h-12 ${meta.color} mx-auto mb-3 opacity-40`} />
            <p className="text-zinc-500 text-sm">
              {segment === "current" ? "Aucune course en cours" : segment === "upcoming" ? "Aucune course a venir" : "Aucun historique"}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bookings-list">
            {bookings.map((b) => (
              <BookingCard key={b.id} booking={b} onClick={() => navigate(`/booking/confirmation?booking_id=${b.id}`)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
